import {
  CustomCheckbox,
  InfiniteScroll,
  ReuseModal,
  ReuseTooltip,
} from "components/ui";
import { memo, useEffect, useRef, useState } from "react";
import { SearchIcon } from "../../../images/SearchIcon";
import { CustomInput } from "../../CustomInput";
import {
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { KeyRoles, ManageSectionsModals } from "../../../../types/enums";
import { KeyRoleMapper } from "../../../company/KeyRoleMapper";
import {
  campaignsApi,
  useGeneratePlaybookForMembersMutation,
  useGetMembersWithoutPlaybookQuery,
} from "../../../../api";
import { useParams } from "react-router-dom";
import { MemberWithoutPlaybook } from "../../../../types/apiTypes";
import { useAppDispatch, useDebounceValue } from "../../../../hooks";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import {
  setIsFetchTableMembers,
  setIsNotificationsOpen,
} from "../../../../features/companySlice";
import { ManageSectionsBlock } from "../../ManageSectionsBlock";
import { AlertInTriangleIcon } from "../../../images";

const ListItem = memo(
  ({
    id,
    fullName,
    title,
    checked,
    onClick,
    role,
    isRetry = false,
  }: {
    id: string;
    fullName: string;
    title: string;
    checked: boolean;
    onClick: () => void;
    role?: KeyRoles;
    isRetry?: boolean;
  }) => {
    return (
      <MenuItem
        sx={{
          padding: "8px 16px 8px 8px",
          justifyContent: "space-between",
          gap: "16px",
        }}
        value={id}
        onClick={onClick}
      >
        <Stack flexDirection={"row"} alignItems={"center"}>
          <CustomCheckbox checked={checked} onChange={() => {}} />
          <Stack>
            <Typography
              component="span"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#232323",
                textWrap: "wrap",
              }}
            >
              {fullName}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                color: "#232323",
                textWrap: "wrap",
              }}
            >
              {title}
            </Typography>
          </Stack>
        </Stack>

        {(role || isRetry) && (
          <Stack flexDirection={"row"} gap={"16px"} alignItems={"center"}>
            {isRetry && (
              <ReuseTooltip
                text={
                  "You’ve already attempted to generate playbook for this prospect."
                }
                hideChildren={false}
                maxWidth={"184px"}
              >
                <AlertInTriangleIcon />
              </ReuseTooltip>
            )}
            {role && <KeyRoleMapper item={role} />}
          </Stack>
        )}
      </MenuItem>
    );
  },
);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  setIsLoading: (isLoading: boolean) => void;
};

const PER_PAGE = 25;

export const GeneratePlaybooksModal = ({
  isOpen,
  onClose,
  setIsLoading,
}: Props) => {
  const { account_id, campaign_id } = useParams();
  const dispatch = useAppDispatch();

  const [searchValue, setSearchValue] = useState<string>("");
  const [members, setMembers] = useState<MemberWithoutPlaybook[]>([]);
  const [selected, setSelected] = useState<{ [key: string]: boolean }>({});
  const [page, setPage] = useState<number>(1);
  const [isHide, setIsHide] = useState<boolean>(false);

  const wrapper = useRef<HTMLDivElement>(null);

  const debounceValue = useDebounceValue(searchValue);

  const [generatePlaybookForMembers, { isLoading: isGeneratingPlaybooks }] =
    useGeneratePlaybookForMembersMutation();

  const {
    data,
    isLoading: isLoadingMembers,
    isFetching,
  } = useGetMembersWithoutPlaybookQuery({
    page,
    per_page: PER_PAGE,
    search: debounceValue.trim() || undefined,
    account_id,
  });

  const canLoadMore = page < (data?.total_pages || 1);

  useEffect(() => {
    setPage(1);
  }, [debounceValue]);

  useEffect(() => {
    setIsLoading(isLoadingMembers);
  }, [isLoadingMembers]);

  useEffect(() => {
    setSelected((prev) => {
      const updated = { ...prev };
      members.forEach(({ id }) => {
        if (!(id in updated)) updated[id] = false;
      });
      return updated;
    });
  }, [members]);

  const selectMemberHandler = (id: string) =>
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const isSelectAll =
    members.length > 0 &&
    members.every((member) => selected[member.id] === true);

  const toggleAllHandler = () => {
    const checked = !isSelectAll;
    setSelected((prev) =>
      Object.fromEntries(Object.keys(prev).map((key) => [key, checked])),
    );
  };

  const selectedIds = Object.entries(selected)
    .filter(([_, value]) => value)
    .map(([key]) => key);

  const generatePlaybooksHandler = () => {
    if (campaign_id) {
      generatePlaybookForMembers({
        campaign_id,
        ids: selectedIds,
        include_all: isSelectAll,
      })
        .unwrap()
        .then(() => {
          dispatch(setIsFetchTableMembers(true));
          dispatch(
            campaignsApi.util.invalidateTags(["activities", "role-members"]),
          );
          dispatch(setIsNotificationsOpen(true));

          onClose();
        })
        .catch((err) =>
          toast.error(
            <CustomToast
              type="error"
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
              title="Playbook generation failed."
            />,
          ),
        );
    }
  };

  if (!data) return null;

  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate BDR Engagement Playbooks"
      maxWidth="540px"
      submitBtnText="Generate Guides"
      submitBtnColor="#1C3C6C"
      onSubmit={generatePlaybooksHandler}
      isLoading={isGeneratingPlaybooks}
      isDisabled={isGeneratingPlaybooks || selectedIds.length === 0}
      isHide={isHide}
      isDisabledCancelBtn={isGeneratingPlaybooks}
    >
      <InfiniteScroll<MemberWithoutPlaybook>
        hasMore={canLoadMore}
        isLoading={isFetching}
        loadMore={() => setPage((prev) => prev + 1)}
        page={page}
        items={data?.items || []}
        scrollOffset={50}
        containerRef={wrapper}
        setAllItems={setMembers}
      >
        {({ items }) => (
          <Stack gap={"16px"}>
            <Stack gap={"8px"}>
              <Stack gap={"12px"}>
                <Typography
                  component="span"
                  variant="subtitle1"
                  sx={{ color: "#464646", fontWeight: 500 }}
                >
                  Which contacts would you like to generate Playbooks for?
                </Typography>
                <CustomInput
                  value={searchValue}
                  placeholder="Search"
                  prefixIcon={
                    isFetching && page === 1 ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  onChange={setSearchValue}
                  clearButton
                  minHeight="42px"
                />
              </Stack>

              <Stack>
                <Stack
                  ref={wrapper}
                  maxHeight={"320px"}
                  sx={{ overflowY: "auto" }}
                >
                  {items.length === 0 && !isFetching && (
                    <Typography
                      variant="body1"
                      sx={{ color: "#797979", padding: "8px 12px" }}
                    >
                      No contacts with such name found
                    </Typography>
                  )}

                  {items.map(({ full_name, job_title, id, role, is_retry }) => (
                    <ListItem
                      key={id}
                      fullName={full_name}
                      title={job_title}
                      onClick={() => selectMemberHandler(id)}
                      id={id}
                      role={role}
                      checked={selected[id] || false}
                      isRetry={is_retry}
                    />
                  ))}
                  {isFetching && (
                    <Stack
                      alignItems={"center"}
                      justifyContent={"center"}
                      flexGrow={1}
                      minHeight={"30px"}
                    >
                      <CircularProgress size={20} sx={{ color: "#1C3C6C" }} />
                    </Stack>
                  )}
                </Stack>

                {items.length > 0 && (
                  <>
                    <Divider style={{ color: "#D9D9D9", margin: 0 }} />
                    <ListItem
                      fullName={`Select all: ${data?.count || 0}`}
                      title=""
                      onClick={toggleAllHandler}
                      id="Select all"
                      checked={isSelectAll}
                    />
                  </>
                )}
              </Stack>
            </Stack>

            <ManageSectionsBlock
              adjustSection={"generated BDR Engagement Playbook"}
              manageSections={"Playbook"}
              hidePrevModalHandler={setIsHide}
              closePrevModalHandler={onClose}
              modal={ManageSectionsModals.playbook}
            />
          </Stack>
        )}
      </InfiniteScroll>
    </ReuseModal>
  );
};
